import React, { useEffect, useState } from "react";
import _ from "lodash";
import ThemedModal from "components/atoms/ThemedModal";
import ThemedList from "components/atoms/ThemedList";
import ListItem from 'theme/ListItem';
import Spinner from "theme/Spinner";
import FetchEmployee from "utils/fetch/FetchEmployee";
import LoadingComponent from "components/atoms/Loading";
import { employeeConstants } from "utils/constants/employee-constants";
import Tag, { tagAppearances } from "components/atoms/Tag";
import { constantsActive } from "utils/constants/constants";
import { Linking, StyleSheet, View } from "react-native";
import i18n from "utils/i18n";
import MutateScan from 'utils/mutate/scan'
import { scanThroughConstants } from "utils/constants/scan-constants";
import EmployeeCard, { EmployeeCardModes } from "components/molecules/EmployeeCard";
import HelperString from 'utils/helper-string'
import { useNavigation } from '@react-navigation/native'
import { openUrl } from 'utils/helper-url'
import { colorList } from "utils/colors";

const constantsPhonenumbers = {
	PHONENUMBER: 'phonenumber',
	LANDLINE_PHONENUMBER: 'landline_phonenumber'
}

const ManagementDashboardModalMeetingRoomDetails = ( { building = null, room = null, timeslot = null, timeslots= null, bookings = null, onClose = null } ) => {

	const navigation = useNavigation();

	const [ startEndTime, setStartEndTime ] = useState( { start: '23:59:59', end: '00:00:00' } );
	const [ visibleTimeslots, setVisibleTimeslots ] = useState( {} );
	const [ visibleEmployees, setVisibleEmployees ] = useState( {} );
	const [ visibleEmployeesLoading, setVisibleEmployeesLoading ] = useState( false );
	const [ scanInMutating, setScanInMutating ] = useState( false );

	const callPhone = ( { employee = {} , which = null } ) => {
		const phonenumber = _.get( employee, which, null );

		if( phonenumber !== null ) {
			if( Linking.canOpenURL( `tel:${phonenumber}` ) ) {
				openUrl( `tel:${phonenumber}` );
			}
		}
	}

	const scanIn = ( { employee = null, callbackStart = null, callbackSuccess = null, callbackError = null } ) => {
		MutateScan.scanIn( `building:${building.guid}`, {
			employeeGuid: employee.guid,
			scannedInThrough: scanThroughConstants.QRCODE,
			callbackStart: callbackStart,
			callbackSuccess: callbackSuccess,
			callbackError: callbackError
		});
	}

	const scanOut = ( { employee = null, callbackStart = null, callbackSuccess = null, callbackError = null } ) => {
		MutateScan.scanOut( `building:${building.guid}`, {
			employeeGuid: employee.guid,
			scannedOutThrough: scanThroughConstants.QRCODE,
			callbackStart: callbackStart,
			callbackSuccess: callbackSuccess,
			callbackError: callbackError
		});
	}

	const fetchEmployees = () => {
		if( _.size( visibleTimeslots ) === 0 ) {
			return;
		}

		let employees = {};
		const employeeGuids = [];
		_.forEach( visibleTimeslots, ( visibleTimeslot ) => {
			_.forEach( _.get( bookings, visibleTimeslot.guid, [] ), ( timeslotBooking ) => {
				if( !( timeslotBooking.employee.guid in employees ) ) {
					employeeGuids.push( timeslotBooking.employee.guid );
				}
			});
		});

		FetchEmployee.fetchAll( {
			guid: employeeGuids,
			dataSet: 'complex',
			variables: {
				visible_for_employees: [
					constantsActive.YES,
					constantsActive.NO
				],
				kind: [
					employeeConstants.KIND_INTERNAL,
					employeeConstants.KIND_EXTERNAL,
				],
			},
			callbackStart: setVisibleEmployeesLoading.bind( this, true ),
			callbackError: setVisibleEmployeesLoading.bind( this, false ),
			/* TODO optimize */
			callbackSuccess: ( { employees = null } ) => {
				let detailedEmployees = {};
				_.forEach( employees, ( employee ) => {
					detailedEmployees[ employee.guid ] = employee;
				})

				setVisibleEmployeesLoading( false );
				setVisibleEmployees( { ...detailedEmployees } );
			}
		});
	}

	useEffect( () => {
		fetchEmployees()

		if( _.size( visibleTimeslots ) === 0 ) {
			return;
		}

		_.forEach( visibleTimeslots, ( visibleTimeslot ) => {
			if ( visibleTimeslot.timeslot_start <= startEndTime.start ) {
				startEndTime.start = visibleTimeslot.timeslot_start;
			}

			if ( visibleTimeslot.timeslot_end >= startEndTime.end ) {
				startEndTime.end = visibleTimeslot.timeslot_end;
			}

			setStartEndTime({
				...startEndTime
			});
		});
	}, [ visibleTimeslots ] );

	useEffect( () => {
		// find the booking that matches this timeslot and if it has an invitation we need to do moar!
		const booking = _.get( bookings, timeslot.guid, null );
		const bookingInvitation = _.get( booking, [ 0, 'booking_invitation' ], null );

		let _visibleTimeslots = [ timeslot ];
		if( bookingInvitation !== null ) {
			let matchedTimeslots = [ [] ];
			let prevMatchedTimeslot = null;
			let matchIndex = 0;

			// loop through all bookings to find all matching timeslots
			_.forEach( timeslots, ( timeslot ) => {
				// check for a match on the invitation
				let tmpBookingInvitation = _.get( bookings, [ timeslot.guid, 0, 'booking_invitation' ], null );

				if( tmpBookingInvitation !== null && tmpBookingInvitation.guid === bookingInvitation.guid ) {
					if( prevMatchedTimeslot === null || prevMatchedTimeslot.timeslot_end === timeslot.timeslot_start ) {
						matchedTimeslots[ matchIndex ].push( timeslot );
						prevMatchedTimeslot = timeslot;
					} else {
						matchIndex++;
						matchedTimeslots[ matchIndex ] = [ timeslot ];
						prevMatchedTimeslot = timeslot;
					}
				}
			});

			// if we found more matches on timeslots (same invitation over a span of multiple grouped times) we need to find 'THE ONE!'
			if( _.size( matchedTimeslots ) > 1 ) {
				matchedTimeslots = _.filter( matchedTimeslots, ( matchedTimeslot ) => {
					let matched = false;
					_.forEach( matchedTimeslot, matchedTimeslotTimeslot => {
						if( matchedTimeslotTimeslot.guid === timeslot.guid ) {
							matched = true;
							return true;
						}
					});

					return matched;
				});
			}

			_visibleTimeslots = matchedTimeslots[ 0 ];
		}

		setVisibleTimeslots( { ..._visibleTimeslots } );
	}, [] );

	// Rendering
	const title = `${ building.name } - ${ room.name }`;
	const subtitle = `${ startEndTime.start.substr( 0, 5 ) } - ${ startEndTime.end.substr( 0, 5 ) }`;

	const renderItem = ( { item = [], index = 0, seperators = {} } ) => {
		let scanInButton = null;
		const hints = [];

		if( visibleEmployeesLoading === false ) {
			if( _.get( item, 'is_in_office', constantsActive.NO ) === constantsActive.YES ) {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-inofficeyes`, item ) }
						appearance={ tagAppearances.GREEN }
						style={ styles.tag }>

						{ i18n.t( 'basic.present_yes' ) }
					</Tag>
				);
			} else {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-inofficeno`, item ) }
						appearance={ tagAppearances.RED }
						style={ styles.tag }>

						{ i18n.t( 'basic.present_no' ) }
					</Tag>
				);
			}
		} else {
			hints.push(
				<Tag
					key={ HelperString.hash( `meeting-room-details-tag-offie-loading`, item ) }
					appearance={ tagAppearances.GREY }
					style={ styles.tag }>

					<Spinner
						style={ styles.spinnerWidth10Height10 }
						color={ colorList.PRIMARY.primary }
					/>
				</Tag>
			);
		}

		if( _.get( item, 'kind', employeeConstants.KIND_INTERNAL) === employeeConstants.KIND_EXTERNAL ) {
			if( scanInMutating === false ) {
				const scanParameters = {
					employee: item,
					callbackError: setScanInMutating.bind( this, false ),
					callbackStart: setScanInMutating.bind( this, true ),
					callbackSuccess: () => {
						setScanInMutating( false );
						fetchEmployees()
					}
				};

				if ( _.get( item, 'is_in_office', constantsActive.NO ) === constantsActive.YES ) {
					scanInButton = (
						<Tag
							key={ HelperString.hash( `meeting-room-details-tag-scanout`, item ) }
							appearance={ tagAppearances.ORANGE }
							style={ styles.tag }
							onPress={ scanOut.bind( this, scanParameters ) }>

							{ i18n.t( 'basic.scan_out' ) }
						</Tag>
					);
				} else {
					scanInButton = (
						<Tag
							key={ HelperString.hash( `meeting-room-details-tag-scanin`, item ) }
							appearance={ tagAppearances.ORANGE }
							style={ styles.tag }
							onPress={ scanIn.bind( this, scanParameters ) }>

							{ i18n.t( 'basic.scan_in' ) }
						</Tag>
					);
				}
			} else {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-spinner`, item ) }
						appearance={tagAppearances.ORANGE}
						style={ styles.tag }>

						<Spinner style={ styles.spinnerWidth10Height10 } />
					</Tag>
				);
			}
		} else {
			if( _.get( item, 'phonenumber', null ) !== null ) {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-phonenumber`, item ) }
						appearance={ tagAppearances.GREY }
						style={ styles.tag }
						nPress={ callPhone.bind( this, { employee: item, which: constantsPhonenumbers.PHONENUMBER } )}>

						{ _.get( item, 'phonenumber', null ) }
					</Tag>
				);
			}

			if( _.get( item, 'landline_phonenumber', null ) !== null ) {
				hints.push(
					<Tag
						key={ HelperString.hash( `meeting-room-details-tag-landline`, item ) }
						appearance={ tagAppearances.GREY }
						style={ styles.tag }
						onPress={ callPhone.bind( this, { employee: item, which: constantsPhonenumbers.LANDLINE_PHONENUMBER } )}>

						{ _.get( item, 'landline_phonenumber', null ) }
					</Tag>
				);
			}
		}

		return	(
			<ListItem
				key={ HelperString.hash( `meeting-room-details-list-item`, item ) }>

				<EmployeeCard
					mode={ EmployeeCardModes.listItem }
					showHeart={ false }
					navigation={ navigation }
					employee={ item }>

					<View style={ styles.tagRow }>
						{ hints }
						{ scanInButton }
					</View>
				</EmployeeCard>
			</ListItem>
		)
	};

	let content = <LoadingComponent />
	if( !visibleEmployeesLoading || _.size( visibleEmployees ) > 0 ) {
		content = (
			<ThemedList
				data={ Object.values( visibleEmployees ) }
				dividerEnabled={ true }
				renderItem={ renderItem.bind( this ) }
			/>
		)
	}

	return (
		<ThemedModal
			visible={ true }
			setVisible={ onClose }
			title={ title }
			subtitle={ subtitle }>

			{ content }
		</ThemedModal>
	)
}

export const styles = StyleSheet.create({
	spinnerWidth10Height10: {
		width: 10,
		height: 10,
	},
	tag: {
		marginRight: 5,
		flexDirection: 'column',
		alignItems: 'flex-start',
		textAlign: 'center'
	},
	tagRow: {
		marginTop: 5,
		flexDirection: 'row',
		alignItems: 'flex-start',
	},
});

export default ManagementDashboardModalMeetingRoomDetails;
